/**
 * The heat history's speed probes (`observer-autogen-heat-history-view`, R15 / criterion 9). Off
 * unless `localStorage['tl:heat-grid-debug'] === '1'`, so they can be switched on in a deployed
 * build (staging) without a rebuild; nothing is recorded otherwise.
 *
 * - `heat-grid:response` when a grid answer arrives from the network (a cached one is not timed);
 * - `heat-grid:painted` when the image has loaded, plus the measure `heat-grid:to-screen` between them;
 * - `window.__heatGridPaints`, the number of times the grid has been rasterised. A zoom or pan must
 *   not move it.
 */
export const HEAT_GRID_DEBUG_KEY = 'tl:heat-grid-debug';

declare global {
  interface Window {
    __heatGridPaints?: number;
  }
}

export const heatGridDebugOn = (): boolean => {
  try {
    return window.localStorage.getItem(HEAT_GRID_DEBUG_KEY) === '1';
  } catch {
    return false; // storage blocked: probes stay off
  }
};

export const markHeatGrid = (point: 'response' | 'painted'): void => {
  if (!heatGridDebugOn() || typeof performance?.mark !== 'function') return;
  if (point === 'response') {
    performance.clearMarks?.('heat-grid:response'); // one response at a time: the newest wins
    performance.mark('heat-grid:response');
    return;
  }
  performance.mark('heat-grid:painted');
  try {
    performance.measure('heat-grid:to-screen', 'heat-grid:response', 'heat-grid:painted');
  } catch {
    // no response mark (a cached combination): nothing to measure
  } finally {
    // Consumed: a later cached paint must not measure from this response.
    performance.clearMarks?.('heat-grid:response');
    performance.clearMarks?.('heat-grid:painted');
  }
};

export const countHeatGridPaint = (): void => {
  if (!heatGridDebugOn()) return;
  window.__heatGridPaints = (window.__heatGridPaints ?? 0) + 1;
};
